import type { DocumentPipelineFilter } from '@kanbu/schema/contracts';
import {
  ContentType,
  SourceType,
  WebsitePageStatus,
} from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { Badge, Button, Dropdown, Input, toast } from '@utima/ui';
import {
  X,
  ClipboardPaste,
  ChartNoAxesColumnIncreasing,
  CircleX,
  DatabaseBackup,
  Download,
  Eye,
  FolderPlus,
  Globe,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Scissors,
  Shield,
  Trash2,
} from 'lucide-react';
import { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { DropdownIconMenuItem, LockedFeatureTooltip } from '@/components';
import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';
import { useEnumTranslations } from '@/hooks';

import type {
  KBContentRow,
  KBFolderData,
  KBExcludeFromKBTarget,
} from './kbTypes';
import type { useKBMutations } from '../hooks/useKBMutations';
import { useDocumentPipelineFilterLabel } from '../hooks/useKBProcessingStatDefinitions';
import type { useKBSelection } from '../hooks/useKBSelection';
import { useKBWebsiteHierarchyLabels } from '../hooks/useKBWebsiteHierarchyLabels';
import { useSourceTypeLabels } from '../hooks/useSourceTypeLabels';
import {
  isKBTrackedPageFilter,
  type KBWebsitePageFilter,
} from '../lib/pageFilter';
import {
  collectDeleteTargets,
  collectExcludeTargets,
  getCurrentFolderDestructiveAction,
  getKBSelectionCutState,
  selectionHasDeleteTarget,
  selectionHasExcludeTarget,
  type KBRowActionContext,
} from '../lib/websiteHierarchy';

export type KBToolbarProps = {
  mode?: 'org' | 'chat';
  folderId: string | null;
  currentFolder?: KBFolderData | null;
  rows: KBContentRow[];
  selection: ReturnType<typeof useKBSelection>;
  mutations: ReturnType<typeof useKBMutations>;
  search: string;
  onSearchChange: (value: string) => void;
  onCreateFolder: () => void;
  onAddDocuments: () => void;
  onDeleteFolder: () => void;
  onFolderSettings: (
    folderId?: string,
    tab?: 'general' | 'auto-update' | 'agents',
  ) => void;
  statusFilter: KBWebsitePageFilter | undefined;
  onStatusFilterChange: (status: KBWebsitePageFilter | undefined) => void;
  documentPipelineFilter?: DocumentPipelineFilter;
  onDocumentPipelineFilterChange?: (
    filter: DocumentPipelineFilter | undefined,
  ) => void;
  sourceTypeFilter?: SourceType;
  onSourceTypeFilterChange?: (sourceType: SourceType | undefined) => void;
  isManagedSource?: boolean;
  onBulkAgentAccess?: () => void;
  onBulkAutoUpdate?: () => void;
  isBulkAutoUpdateLocked?: boolean;
  onBulkWeight?: () => void;
  onPromoteToTrackedWebsite?: () => void;
  onExcludeFromKB?: (target: KBExcludeFromKBTarget) => void;
  onRequestDeleteFolders?: (folderIds: string[]) => void;
  onViewDocument?: (documentId: string) => void;
  onExportDocuments?: (documentIds: string[]) => void;
};

export const KBToolbar = memo(function KBToolbar({
  mode = 'org',
  folderId,
  currentFolder,
  rows,
  selection,
  mutations,
  search,
  onSearchChange,
  onCreateFolder,
  onAddDocuments,
  onDeleteFolder,
  onFolderSettings,
  statusFilter,
  onStatusFilterChange,
  documentPipelineFilter,
  onDocumentPipelineFilterChange,
  sourceTypeFilter,
  onSourceTypeFilterChange,
  isManagedSource,
  onBulkAgentAccess,
  onBulkAutoUpdate,
  isBulkAutoUpdateLocked = false,
  onBulkWeight,
  onPromoteToTrackedWebsite,
  onExcludeFromKB,
  onRequestDeleteFolders,
  onViewDocument,
  onExportDocuments,
}: KBToolbarProps) {
  const { t } = useLingui();
  const enumTranslations = useEnumTranslations();
  const pipelineFilterLabels = useDocumentPipelineFilterLabel();
  const sourceTypeLabels = useSourceTypeLabels();
  const isChatMode = mode === 'chat';
  const websiteHierarchyLabels = useKBWebsiteHierarchyLabels();

  // Source lens (master plan §1A / Phase K4a): reuses the badge icons from
  // `useSourceTypeLabels` so the chip and its row markers stay visually
  // consistent, but keeps the shorter lens-specific copy from the spec.
  const sourceLensOptions = useMemo(
    () => [
      { value: undefined, label: t`All` },
      {
        value: SourceType.Upload,
        label: t`Uploads`,
        icon: sourceTypeLabels[SourceType.Upload].icon,
      },
      {
        value: SourceType.Website,
        label: t`Websites`,
        icon: sourceTypeLabels[SourceType.Website].icon,
      },
      {
        value: SourceType.GoogleDrive,
        label: t`Cloud`,
        icon: sourceTypeLabels[SourceType.GoogleDrive].icon,
      },
      {
        value: SourceType.Rss,
        label: t`Feeds`,
        icon: sourceTypeLabels[SourceType.Rss].icon,
      },
    ],
    [t, sourceTypeLabels],
  );

  // The default variant's search+filters can wrap onto several lines at
  // widths well below where the table itself would need to scroll (the
  // right-side action buttons and the app sidebar both eat into the
  // available row width, so the wrap point isn't tied to any one screen-size
  // breakpoint — it depends on sidebar state, active filters, and which
  // action buttons are shown). Rather than guess a width, measure the
  // default variant's real rendered height and float it as a shared
  // min-height on all three variants, so switching between them (e.g. on
  // row select) never changes the toolbar's height and jumps the table.
  const defaultToolbarRef = useRef<HTMLDivElement>(null);
  const [minToolbarHeight, setMinToolbarHeight] = useState<number>();

  useLayoutEffect(() => {
    const measure = () => {
      const el = defaultToolbarRef.current;

      if (el) {
        setMinToolbarHeight(el.getBoundingClientRect().height);
      }
    };

    measure();
    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  });

  const toolbarShellClassName =
    'flex min-h-11 items-center justify-between gap-2 rounded-lg border py-1.5 px-2';
  const toolbarShellStyle = { minHeight: minToolbarHeight };

  const showCurrentFolderMenu = !isChatMode && !!folderId && !!currentFolder;

  const actionContext: KBRowActionContext = {
    isChatMode,
    isManagedSource: !!isManagedSource,
  };

  const currentFolderDestructiveAction = currentFolder
    ? getCurrentFolderDestructiveAction(currentFolder)
    : null;

  const hasActiveFilters = !!statusFilter || !!documentPipelineFilter;
  const hasSelection = selection.selectedItems.size > 0;
  const isCutMode = !!selection.cutItems;

  // Determine what types are selected
  const selectedTypes = new Set<string>();

  for (const [_id, type] of selection.selectedItems) {
    selectedTypes.add(type);
  }

  const hasDocuments = selectedTypes.has('document');
  const hasFolders = selectedTypes.has('folder');
  const hasPages = selectedTypes.has('page');
  const isSingleSelection = selection.selectedItems.size === 1;
  const onlyPages = hasPages && !hasDocuments && !hasFolders;
  const onlyDocumentsOrFolders = !hasPages && (hasDocuments || hasFolders);
  const singleFolderSelected =
    hasFolders && !hasDocuments && !hasPages && isSingleSelection;
  const singleDocumentSelected =
    hasDocuments && !hasFolders && !hasPages && isSingleSelection;
  const selectedDocumentId = singleDocumentSelected
    ? (selection.selectedItems.keys().next().value as string | undefined)
    : undefined;
  const hasSettingsSelection = hasDocuments || hasFolders;

  // Check if selected pages are New/Untracked (for "Add to KB" action)
  const selectedPageStatuses = new Set<string>();
  const selectedWebsiteDocuments: string[] = [];
  let hasManualUploadedDocumentSelected = false;
  let hasRssDocumentSelected = false;

  for (const [id] of selection.selectedItems) {
    const row = rows.find(r => r.id === id);

    if (row?.rowType === 'document' && row.data.sourceType === SourceType.Rss) {
      hasRssDocumentSelected = true;
    }

    if (row?.rowType === 'page') {
      selectedPageStatuses.add(row.data.status);
    }

    if (row?.rowType === 'document' && row.data.type === ContentType.Website) {
      selectedWebsiteDocuments.push(row.id);
    } else if (
      row?.rowType === 'document' &&
      row.data.type === ContentType.Document
    ) {
      hasManualUploadedDocumentSelected = true;
    }
  }

  const handlePaste = async () => {
    if (!selection.cutItems || selection.cutItems.size === 0) return;

    const folderIds: string[] = [];
    const documentIds: string[] = [];

    for (const [id, type] of selection.cutItems) {
      if (type === 'folder') {
        folderIds.push(id);
      } else if (type === 'document') {
        documentIds.push(id);
      }
    }

    try {
      // Move folders one by one
      for (const id of folderIds) {
        await mutations.moveFolder.mutateAsync({
          id,
          newParentId: folderId,
        });
      }

      // Bulk move documents
      if (documentIds.length > 0) {
        await mutations.bulkMove.mutateAsync({
          documentIds,
          folderId,
        });
      }

      selection.completePaste();
      toast.success(t`Items moved successfully`);
    } catch {
      toast.error(t`Failed to move items`);
    }
  };

  const handleBulkDelete = async () => {
    const { pageIds, documentIds, folderIds } = collectDeleteTargets(
      rows,
      selection.selectedItems.keys(),
      actionContext,
    );

    if (
      pageIds.length === 0 &&
      documentIds.length === 0 &&
      folderIds.length === 0
    ) {
      return;
    }

    if (pageIds.length > 0) {
      try {
        await mutations.deleteRemovedPages.mutateAsync({ pageIds });
        toast.success(t`Pages deleted`);
      } catch {
        toast.error(t`Failed to delete pages`);

        return;
      }
    }

    if (documentIds.length > 0) {
      try {
        await mutations.bulkDelete.mutateAsync({ documentIds });
        toast.success(
          documentIds.length === 1 ? t`Document deleted` : t`Documents deleted`,
        );
      } catch {
        toast.error(t`Failed to delete documents`);

        return;
      }
    }

    selection.clearSelection();

    if (folderIds.length > 0) {
      onRequestDeleteFolders?.(folderIds);
    }
  };

  const handleBulkRefresh = async () => {
    const documentIds: string[] = [];

    for (const [id, type] of selection.selectedItems) {
      if (type === 'document') {
        documentIds.push(id);
      }
    }

    if (documentIds.length === 0) return;

    try {
      await mutations.bulkRefresh.mutateAsync({ documentIds });
      selection.clearSelection();
      toast.success(t`Documents queued for refresh`);
    } catch {
      toast.error(t`Failed to refresh documents`);
    }
  };

  const handleIncludePages = async () => {
    const pageIds: string[] = [];

    for (const [id, type] of selection.selectedItems) {
      if (type === 'page') {
        pageIds.push(id);
      }
    }

    if (pageIds.length === 0) return;

    try {
      await mutations.includePages.mutateAsync({ pageIds });
      selection.clearSelection();
      toast.success(t`Pages added to Knowledge Base`);
    } catch {
      toast.error(t`Failed to add pages`);
    }
  };

  const handleDismissPages = async () => {
    const pageIds: string[] = [];

    for (const [id, type] of selection.selectedItems) {
      if (type === 'page') {
        pageIds.push(id);
      }
    }

    if (pageIds.length === 0) return;

    try {
      await mutations.dismissPages.mutateAsync({ pageIds });
      selection.clearSelection();
      toast.success(t`Pages dismissed`);
    } catch {
      toast.error(t`Failed to dismiss pages`);
    }
  };

  const handleExcludeFromKb = async () => {
    const { pageIds, documentIds, folderIds } = collectExcludeTargets(
      rows,
      selection.selectedItems.keys(),
      actionContext,
    );

    if (
      pageIds.length === 0 &&
      documentIds.length === 0 &&
      folderIds.length === 0
    ) {
      return;
    }

    const skippedManualCount = [...selection.selectedItems.keys()].filter(
      id => {
        const row = rows.find(r => r.id === id);

        return (
          row?.rowType === 'document' && row.data.type === ContentType.Document
        );
      },
    ).length;

    try {
      await mutations.excludePages.mutateAsync({
        pageIds,
        documentIds,
        folderIds,
      });

      selection.clearSelection();
      toast.success(t`Removed from tracked pages`);

      if (skippedManualCount > 0) {
        toast.info(
          t`${skippedManualCount} manually uploaded document(s) were not changed`,
        );
      }
    } catch {
      toast.error(t`Failed to remove from tracked pages`);
    }
  };

  // Cut mode: show paste controls
  if (isCutMode) {
    return (
      <div
        className={`${toolbarShellClassName} border border-dashed border-primary/40 bg-primary/5`}
        style={toolbarShellStyle}
      >
        <div className='flex items-center gap-2 text-sm'>
          <Scissors className='size-4 text-primary' />
          <Trans>
            Moving {selection.cutItems!.size} item(s) — navigate to destination
          </Trans>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            variant='ghost'
            size='sm'
            outline
            onClick={() => selection.cancelCut()}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            size='sm'
            disabled={
              !selection.canPasteHere(folderId) || isManagedSource || isChatMode
            }
            onClick={handlePaste}
            icon={<ClipboardPaste className='size-4' />}
          >
            <Trans>Paste here</Trans>
          </Button>
        </div>
      </div>
    );
  }

  // Selection mode
  if (hasSelection) {
    const showBulkExcludeFromKb = selectionHasExcludeTarget(
      rows,
      selection.selectedItems.keys(),
      actionContext,
    );
    const hasDeleteTarget = selectionHasDeleteTarget(
      rows,
      selection.selectedItems.keys(),
      actionContext,
    );
    const cutState = getKBSelectionCutState(
      rows,
      selection.selectedItems.keys(),
      actionContext,
      websiteHierarchyLabels,
    );
    const showCut = onlyDocumentsOrFolders && cutState.enabled;
    const showCutDisabledInMenu = onlyDocumentsOrFolders && !cutState.enabled;
    const showRefresh = hasDocuments;
    const showExport = hasDocuments && !!onExportDocuments;
    const showEditFolder = singleFolderSelected;
    const showCutInMenu = onlyDocumentsOrFolders;
    const showBulkAgentAccess = hasSettingsSelection && !!onBulkAgentAccess;
    const showBulkAutoUpdateMenu = hasSettingsSelection && !!onBulkAutoUpdate;
    const showBulkPriority = onlyDocumentsOrFolders && !!onBulkWeight;
    const showPromoteWebsite =
      hasDocuments &&
      !hasFolders &&
      !hasPages &&
      !hasManualUploadedDocumentSelected &&
      selectedWebsiteDocuments.length > 0 &&
      !isManagedSource &&
      !!onPromoteToTrackedWebsite;
    const showBulkDelete = onlyDocumentsOrFolders || hasDeleteTarget;
    const hasMoreMenuContent =
      showExport ||
      showCutInMenu ||
      showBulkAgentAccess ||
      showBulkAutoUpdateMenu ||
      showBulkPriority ||
      showPromoteWebsite ||
      showBulkDelete;

    const moreMenuItems = (
      <>
        {showExport && (
          <Dropdown.Item
            className='flex items-center gap-2'
            onClick={() => {
              const documentIds = [...selection.selectedItems]
                .filter(([, type]) => type === 'document')
                .map(([id]) => id);
              onExportDocuments?.(documentIds);
            }}
          >
            <Download className='size-4' />
            <Trans>Download / export</Trans>
          </Dropdown.Item>
        )}

        {showCutInMenu && (
          <DropdownIconMenuItem
            icon={Scissors}
            label={<Trans>Cut</Trans>}
            disabled={showCutDisabledInMenu}
            disabledTooltip={
              showCutDisabledInMenu ? cutState.disabledTooltip : undefined
            }
            onClick={showCut ? () => selection.startCut(folderId) : undefined}
          />
        )}

        {showBulkAgentAccess && (
          <Dropdown.Item
            className='flex items-center gap-2'
            onClick={onBulkAgentAccess}
          >
            <Shield className='size-4' />
            <Trans>Agent access</Trans>
          </Dropdown.Item>
        )}

        {showBulkAutoUpdateMenu && (
          <>
            {isBulkAutoUpdateLocked ? (
              <LockedFeatureTooltip locked>
                <Dropdown.Item
                  className='flex items-center gap-2 opacity-50'
                  disabled
                >
                  <Lock className='size-4' />
                  <Trans>Auto-update Settings</Trans>
                </Dropdown.Item>
              </LockedFeatureTooltip>
            ) : hasManualUploadedDocumentSelected ? (
              <SimpleTooltip
                title={t`Auto-update is not available when manually uploaded documents are selected`}
              >
                <span className='block'>
                  <Dropdown.Item
                    className='flex items-center gap-2 opacity-50'
                    disabled
                  >
                    <RefreshCw className='size-4' />
                    <Trans>Auto-update Settings</Trans>
                  </Dropdown.Item>
                </span>
              </SimpleTooltip>
            ) : (
              <Dropdown.Item
                className='flex items-center gap-2'
                onClick={onBulkAutoUpdate}
              >
                <RefreshCw className='size-4' />
                <Trans>Auto-update Settings</Trans>
              </Dropdown.Item>
            )}
          </>
        )}

        {showBulkPriority && (
          <Dropdown.Item
            className='flex items-center gap-2'
            onClick={onBulkWeight}
          >
            <ChartNoAxesColumnIncreasing className='size-4' />
            <Trans>Priority</Trans>
          </Dropdown.Item>
        )}

        {showPromoteWebsite && (
          <DropdownIconMenuItem
            icon={Globe}
            label={<Trans>Promote to website hierarchy</Trans>}
            hint={websiteHierarchyLabels.promoteToWebsiteHierarchyHint}
            onClick={onPromoteToTrackedWebsite}
          />
        )}

        {showBulkDelete && (
          <>
            <Dropdown.Separator />
            <DropdownIconMenuItem
              icon={Trash2}
              className='text-danger'
              label={<Trans>Delete</Trans>}
              disabled={
                !hasDeleteTarget ||
                mutations.bulkDelete.isPending ||
                mutations.deleteFolder.isPending ||
                mutations.deleteRemovedPages.isPending
              }
              disabledTooltip={
                hasDeleteTarget
                  ? undefined
                  : websiteHierarchyLabels.removeFromTrackedPagesDisclaimer
              }
              onClick={handleBulkDelete}
            />
          </>
        )}
      </>
    );

    const showPageAddToKb =
      onlyPages &&
      (selectedPageStatuses.has(WebsitePageStatus.New) ||
        selectedPageStatuses.has(WebsitePageStatus.Untracked));

    const showPageIgnore =
      onlyPages && selectedPageStatuses.has(WebsitePageStatus.New);

    return (
      <div
        className={`${toolbarShellClassName} border border-primary/20 bg-primary/5`}
        style={toolbarShellStyle}
      >
        <div className='flex items-center gap-3'>
          <span className='text-sm font-medium'>
            <Trans>{selection.selectedItems.size} selected</Trans>
          </span>
          <button
            className='text-xs text-primary underline'
            onClick={() =>
              selection.selectAll(
                rows.map(r => ({ id: r.id, type: r.rowType })),
              )
            }
          >
            <Trans>Select all</Trans>
          </button>
          <button
            className='text-xs text-muted-fg underline'
            onClick={() => selection.clearSelection()}
          >
            <Trans>Deselect</Trans>
          </button>
        </div>
        <div className='flex items-center gap-1'>
          {showPageAddToKb && (
            <Button
              variant='secondary'
              size='sm'
              onClick={handleIncludePages}
              loading={mutations.includePages.isPending}
              icon={<Plus className='size-4' />}
            >
              {websiteHierarchyLabels.addToTrackedPagesLabel}
            </Button>
          )}

          {showPageIgnore && (
            <Button
              variant='ghost'
              size='sm'
              outline
              onClick={handleDismissPages}
              loading={mutations.dismissPages.isPending}
            >
              <Trans>Ignore</Trans>
            </Button>
          )}

          {showBulkExcludeFromKb && (
            <Button
              variant='ghost'
              size='sm'
              outline
              onClick={handleExcludeFromKb}
              loading={mutations.excludePages.isPending}
              icon={<CircleX className='size-4' />}
            >
              {websiteHierarchyLabels.removeFromTrackedPagesLabel}
            </Button>
          )}

          {showEditFolder && (
            <Button
              variant='ghost'
              size='sm'
              outline
              onClick={() => {
                const selectedId = selection.selectedItems.keys().next().value;

                if (selectedId) onFolderSettings(selectedId);
              }}
              icon={<Pencil className='size-4' />}
            >
              <Trans>Edit</Trans>
            </Button>
          )}

          {showRefresh && (
            <Button
              variant='ghost'
              size='sm'
              outline
              onClick={handleBulkRefresh}
              loading={mutations.bulkRefresh.isPending}
              title={
                hasRssDocumentSelected
                  ? t`Refresh data reprocesses these entries. Use Sync now on the feed to fetch new or updated feed content.`
                  : undefined
              }
              icon={<DatabaseBackup className='size-4' />}
            >
              <Trans>Refresh Data</Trans>
            </Button>
          )}

          {selectedDocumentId && onViewDocument && (
            <SimpleTooltip title={t`View details`}>
              <Button
                variant='ghost'
                size='sm'
                outline
                onClick={() => onViewDocument(selectedDocumentId)}
                icon={<Eye className='size-4' />}
                aria-label={t`View details`}
              />
            </SimpleTooltip>
          )}

          {hasMoreMenuContent && (
            <Dropdown.Root modal={false}>
              <Dropdown.Trigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  outline
                  icon={<MoreHorizontal className='size-4' />}
                  aria-label={t`More actions`}
                />
              </Dropdown.Trigger>
              <Dropdown.Content side='bottom' align='end'>
                {moreMenuItems}
              </Dropdown.Content>
            </Dropdown.Root>
          )}
        </div>
      </div>
    );
  }

  // Default state
  return (
    <div
      ref={defaultToolbarRef}
      className={`${toolbarShellClassName} border-transparent`}
      style={toolbarShellStyle}
    >
      <div className='flex flex-wrap items-center gap-2'>
        {/* Search — wrapped in a fixed-width shrink-0 div because Input's own
            wrapper is `w-full`; as a direct flex-wrap child that would claim
            the whole row and push the filters below it. */}
        <div className='w-48 shrink-0'>
          <Input
            type='search'
            placeholder={t`Search...`}
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            className='h-8'
          />
        </div>

        {onSourceTypeFilterChange && (
          <div className='flex flex-wrap items-center gap-1'>
            {sourceLensOptions.map(option => {
              const isActive = sourceTypeFilter === option.value;
              const Icon = option.icon;

              return (
                <Badge
                  key={option.value ?? 'all'}
                  variant={isActive ? 'primary' : 'secondary'}
                  outline={!isActive}
                  size='sm'
                  className='cursor-pointer gap-1'
                  onClick={() => onSourceTypeFilterChange(option.value)}
                >
                  {Icon && <Icon className='size-3' />}
                  {option.label}
                </Badge>
              );
            })}
          </div>
        )}

        {hasActiveFilters && (
          <div className='flex flex-wrap items-center gap-1'>
            {statusFilter && (
              <Badge variant='secondary' size='sm' className='gap-1 pr-1'>
                {isKBTrackedPageFilter(statusFilter)
                  ? t`Tracked`
                  : enumTranslations.websitePageStatus[statusFilter]}
                <button
                  type='button'
                  className='rounded p-0.5 hover:bg-muted'
                  aria-label={t`Clear filter`}
                  onClick={() => onStatusFilterChange(undefined)}
                >
                  <X className='size-3' />
                </button>
              </Badge>
            )}
            {documentPipelineFilter && onDocumentPipelineFilterChange && (
              <Badge variant='secondary' size='sm' className='gap-1 pr-1'>
                {pipelineFilterLabels[documentPipelineFilter]}
                <button
                  type='button'
                  className='rounded p-0.5 hover:bg-muted'
                  aria-label={t`Clear filter`}
                  onClick={() => onDocumentPipelineFilterChange(undefined)}
                >
                  <X className='size-3' />
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>

      <div className='flex shrink-0 items-center gap-2'>
        {!isChatMode && !isManagedSource && (
          <Button
            variant='ghost'
            size='sm'
            outline
            onClick={onCreateFolder}
            icon={<FolderPlus className='size-4' />}
          >
            <Trans>New Folder</Trans>
          </Button>
        )}

        {(!isManagedSource || isChatMode) && (
          <Button
            size='sm'
            onClick={onAddDocuments}
            icon={<Plus className='size-4' />}
          >
            <Trans>Add Documents</Trans>
          </Button>
        )}

        {showCurrentFolderMenu && (
          <Dropdown.Root modal={false}>
            <Dropdown.Trigger asChild>
              <Button
                variant='ghost'
                size='sm'
                outline
                icon={<MoreHorizontal className='size-4' />}
                aria-label={t`Folder actions`}
              />
            </Dropdown.Trigger>
            <Dropdown.Content side='bottom' align='end'>
              <Dropdown.Item
                className='flex items-center gap-2'
                onClick={() => onFolderSettings()}
              >
                <Pencil className='size-4' />
                <Trans>Edit</Trans>
              </Dropdown.Item>

              <Dropdown.Item
                className='flex items-center gap-2'
                onClick={() => onFolderSettings(undefined, 'agents')}
              >
                <Shield className='size-4' />
                <Trans>Agent access</Trans>
              </Dropdown.Item>

              {onBulkAutoUpdate && (
                <>
                  {isBulkAutoUpdateLocked ? (
                    <LockedFeatureTooltip locked>
                      <Dropdown.Item
                        className='flex items-center gap-2 opacity-50'
                        disabled
                      >
                        <Lock className='size-4' />
                        <Trans>Auto-update Settings</Trans>
                      </Dropdown.Item>
                    </LockedFeatureTooltip>
                  ) : (
                    <Dropdown.Item
                      className='flex items-center gap-2'
                      onClick={() => onFolderSettings(undefined, 'auto-update')}
                    >
                      <RefreshCw className='size-4' />
                      <Trans>Auto-update Settings</Trans>
                    </Dropdown.Item>
                  )}
                </>
              )}

              {currentFolderDestructiveAction && (
                <>
                  <Dropdown.Separator />
                  {currentFolderDestructiveAction === 'exclude' &&
                    onExcludeFromKB && (
                      <DropdownIconMenuItem
                        icon={CircleX}
                        label={
                          websiteHierarchyLabels.removeFromTrackedPagesLabel
                        }
                        hint={
                          websiteHierarchyLabels.removeFromTrackedPagesDisclaimer
                        }
                        onClick={() =>
                          onExcludeFromKB({
                            folderIds: [currentFolder!.id],
                          })
                        }
                      />
                    )}
                  {currentFolderDestructiveAction && (
                    <DropdownIconMenuItem
                      icon={Trash2}
                      className='text-danger'
                      label={<Trans>Delete Folder</Trans>}
                      disabled={currentFolderDestructiveAction !== 'delete'}
                      disabledTooltip={
                        websiteHierarchyLabels.removeFromTrackedPagesDisclaimer
                      }
                      onClick={onDeleteFolder}
                    />
                  )}
                </>
              )}
            </Dropdown.Content>
          </Dropdown.Root>
        )}
      </div>
    </div>
  );
});
